/**
 * A chave que faz `Uber`, `Uber Uber *Trip Help.U` e `Uber* Trip` serem o
 * mesmo estabelecimento.
 *
 * ## O problema, medido nas transações reais desta casa
 *
 * 2272 saídas produzem **966 descrições distintas**. Uber aparece com **sete
 * grafias** em 165 transações; `211 Liv Ctba`, `219 Liv Ctba` e `221 Liv Ctba`
 * são a mesma loja com o número do terminal grudado na frente; e **452
 * descrições carregam o sufixo de parcela** `3/4`, que faz cada parcela da
 * mesma compra virar um estabelecimento diferente.
 *
 * Normalizar leva 966 a **558**. Cada regra abaixo foi medida antes de entrar,
 * a que não pagou ficou de fora, e uma que parecia pagar foi apagada depois de
 * a varredura de mutação mostrar que ela não fazia nada (decisão 198).
 *
 * ## Sem IA, e a nota diz por quê
 *
 * `Merchant.md`: *"É casamento de string, e casamento de string resolve bem o
 * suficiente aqui. IA fica reservada para nota fiscal, onde o problema é de
 * fato difícil."*
 */

/**
 * Marketplaces e apps: o que vem **antes** do `*` é o estabelecimento, e o que
 * vem depois é o vendedor ou a corrida, que muda a cada compra.
 *
 * `Shopee *Decorporto` e `Shopee *Jatobraz` são duas compras na Shopee, não
 * duas lojas. Sem esta lista, cada vendedor da Shopee vira um estabelecimento
 * com uma transação só — a cauda longa que torna a classificação impossível.
 */
const MARKETPLACES: Record<string, string> = {
  shopee: 'shopee',
  amazonmktplc: 'amazon',
  mercadolivre: 'mercado livre',
  uber: 'uber',
  ifd: 'ifood',
  airbnb: 'airbnb',
  'transp colet': 'transporte coletivo',
}

/**
 * Adquirentes e processadores: o que vem **antes** do `*` é quem processou o
 * pagamento, e o estabelecimento é o que vem **depois**.
 *
 * É o inverso dos marketplaces, e confundir os dois é o erro caro: tratar
 * `Pag*` como estabelecimento juntaria toda loja que usa PagSeguro num
 * estabelecimento só.
 */
const ADQUIRENTES = new Set([
  'dl',
  'mp',
  'pag',
  'pg',
  'payu',
  'ec',
  'zp',
  'stb',
  'pb',
  'nuv',
  'ebw',
  'app',
  'pagamento',
])

/**
 * A chave de agrupamento de uma descrição bancária. `''` quando não sobra nada
 * — e quem chama trata isso como "não dá para agrupar", nunca como um grupo.
 */
/** Tira os diacríticos sem tabela de "á→a" para alguém manter. */
function semAcento(raw: string): string {
  return raw.normalize('NFD').replace(/\p{Diacritic}/gu, '')
}

export function merchantKeyOf(description: string): string {
  let s = semAcento(description).toLowerCase()

  // 1. `Transferência enviada|Karen Gubert` — quem importa é a contraparte.
  //    Sem isto, 153 transferências para pessoas diferentes viram um
  //    estabelecimento só chamado "transferência enviada", e categorizar o
  //    grupo classificaria errado todas menos uma.
  const barra = s.indexOf('|')
  if (barra !== -1) s = s.slice(barra + 1)

  // 2. O `*`, que significa uma coisa ou o inverso dela conforme quem vem antes.
  const estrela = s.indexOf('*')
  if (estrela !== -1) {
    const antes = s.slice(0, estrela).trim()
    const depois = s.slice(estrela + 1).trim()
    const marketplace = Object.keys(MARKETPLACES).find((k) => antes.startsWith(k))
    if (marketplace !== undefined) return MARKETPLACES[marketplace] ?? ''
    if (ADQUIRENTES.has(antes.replace(/\./g, ''))) s = depois
  }

  // 3. Pontuação vira espaço, e número solto some — `Raia2537` e `Raia` são a
  //    mesma farmácia, e `211 Liv Ctba` é a mesma livraria de `219 Liv Ctba`.
  //    O corte entre letra e dígito é o que faz `Raia2537` virar `raia 2537`
  //    antes de o número ser descartado.
  //
  //    **É também isto que resolve o sufixo de parcela**, que era 452 das 2272
  //    descrições: `Leroy Merlin 2/6` perde a barra aqui e vira `leroy merlin`
  //    com `2` e `6` descartados junto com os outros números. Houve uma regra
  //    própria para parcela, e ela foi apagada: a varredura de mutação mostrou
  //    que apagá-la não matava teste nenhum, e a medição confirmou o motivo —
  //    **zero** das 2272 descrições mudam de chave com ou sem ela. Ver a
  //    decisão 198.
  s = s
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/([a-z])(\d)/g, '$1 $2')
    .split(' ')
    .filter((t) => t !== '' && !/^\d+$/.test(t))
    .join(' ')

  // A cidade NÃO é removida, e isso contraria a letra de `Merchant.md`.
  // Medido nas transações reais: remover `ctba|curitiba|curit|ct|cwb|pr` leva
  // de 558 chaves para 557 — uma. Em troca, arriscaria fundir duas lojas que só
  // se distinguem pelo lugar. A regra existe na nota para um problema que estes
  // dados não têm; entra no dia em que tiverem.
  return s
}
